import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
	Box,
	Button,
	CircularProgress,
	ClickAwayListener,
	Popover,
	Typography,
} from '@mui/material';
import store from 'store';

import { downloadFileByUrl } from '@common/helpers/downloadFile';
import { ICertificateModel } from '@common/models/Certificates/Certificate';

interface DownloadPopoverProps {
	certificateModel: ICertificateModel;
}

function DownloadPopover({ certificateModel }: DownloadPopoverProps) {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const anchorRef = useRef<HTMLButtonElement | null>(null);
	const [loading, setLoading] = useState(false);

	const handleToggle = () => {
		setOpen((prev) => !prev);
	};

	const handleClose = () => {
		setOpen(false);
	};

	const handleDownload = async (event: React.MouseEvent<HTMLButtonElement>, isPDF: boolean) => {
		event.preventDefault();
		try {
			setLoading(true);
			if (certificateModel.pdfUrl) {
				await downloadFileByUrl(
					isPDF ? certificateModel.pdfUrl : certificateModel.pngUrl,
					`${certificateModel.name} Сertificate - ${store.profile.last_name}`,
				);
			} else {
				await certificateModel.createStudentFinalCertificate(store.currentStudentId);
				await downloadFileByUrl(
					isPDF ? certificateModel.filePdfUrl : certificateModel.filePngUrl,
					`${certificateModel.name} Сertificate - ${store.profile.last_name}`,
				);
			}
		} catch (error) {
			console.error('Error downloading file:', error);
		} finally {
			setLoading(false);
		}
	};

	return (
		<>
			<Button
				variant="contained"
				size="small"
				onClick={handleToggle}
				ref={anchorRef}
				sx={{ fontSize: '14px' }}
				disabled={!certificateModel.certificateLevel && !certificateModel.pdfUrl}
				fullWidth
			>
				{t('certificates.download')} <KeyboardArrowDownIcon />
			</Button>
			<Popover
				open={open}
				anchorEl={anchorRef.current}
				onClose={handleClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'left',
				}}
				PaperProps={{
					sx: {
						borderRadius: 2,
						p: 2,
						mt: 1,
						width: 290,
						boxShadow: 4,
					},
				}}
			>
				<ClickAwayListener onClickAway={handleClose}>
					<Box display="flex" flexDirection="column" gap="8px">
						<Box display="flex" alignItems="center">
							<Typography variant="body2">{t('certificates.downloadText')}</Typography>
						</Box>
						<Button
							variant="contained"
							size="small"
							sx={{ fontSize: '14px' }}
							disabled={loading}
							startIcon={loading ? <CircularProgress color="inherit" size={15} /> : null}
							onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleDownload(e, false)}
							fullWidth
						>
							PNG
						</Button>
						<Button
							variant="contained"
							size="small"
							sx={{ fontSize: '14px' }}
							disabled={loading}
							startIcon={loading ? <CircularProgress color="inherit" size={15} /> : null}
							onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleDownload(e, true)}
							fullWidth
						>
							PDF
						</Button>
					</Box>
				</ClickAwayListener>
			</Popover>
		</>
	);
}

export default DownloadPopover;
